import CircularProgress from "@mui/material/CircularProgress";
import { UserContext } from "App";
import { debounce } from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  CreateBundleCMS,
  CreateBundleIMS,
  FilterProduct,
  GenerateBundleBarcode,
  getInventoryData,
} from "../../../../../../../../config/Services";
import { useSnackbar } from "../../../../../hooks/SnackbarProvider";
import { useAddToCart } from "../../../../../hooks/useAddToCart";
import useWeighingItemModal from "../../../../../hooks/useWeighingItemModal";
import BatchSelectionModal from "../batch-popup";
import "./index.css";

/* ── Weight-unit label formatting (same as keyboardDropDownModal) ── */
const WEIGHT_UNIT_LABELS = {
  kg: "KG",
  kgs: "KG",
  kilogram: "KG",
  kilograms: "KG",
  ltr: "Liter",
  ltrs: "Liter",
  litre: "Liter",
  litres: "Liter",
  liter: "Liter",
  liters: "Liter",
  l: "Liter",
  gm: "Gram",
  g: "Gram",
  gms: "Gram",
  grm: "Gram",
  gram: "Gram",
  grams: "Gram",
  ml: "ML",
  milliliter: "ML",
  milliliters: "ML",
  millilitre: "ML",
  millilitres: "ML",
  nos: "Nos",
  no: "Nos",
  pc: "Pieces",
  pcs: "Pieces",
  piece: "Pieces",
  pieces: "Pieces",
  each: "Each",
};

const formatWeightUnit = (unit) =>
  unit ? WEIGHT_UNIT_LABELS[unit.toString().trim().toLowerCase()] || unit : "";

/* Same check as keyboardDropDownModal: only the explicit flag marks a scale item */
const isWeighingProduct = (product) =>
  product?.needsWeighingScaleIntegration === true ||
  product?.variant?.needsWeighingScaleIntegration === true;

const UOM_OPTIONS = ["gms", "kg", "g", "ml", "L", "ltr", "pcs", "nos", "units", "oz"];

/* ── localStorage helpers ── */
const BUNDLES_KEY = "pallet_saved_bundles";
const loadSavedBundles = () => {
  try {
    return JSON.parse(localStorage.getItem(BUNDLES_KEY) || "[]");
  } catch {
    return [];
  }
};
const persistBundles = (list) => localStorage.setItem(BUNDLES_KEY, JSON.stringify(list));

/* Default bundle name: "POS Bundle 30 Jul 6:30 pm" */
const buildDefaultBundleName = () => {
  const d = new Date();
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let h = d.getHours();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `POS Bundle ${d.getDate()} ${months[d.getMonth()]} ${h}:${mins} ${ampm}`;
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Fallback local generator — only used if the API call fails */
const generateBundleBarcodeFallback = () => `55555${String(Date.now()).slice(-8)}`;

/**
 * Normalize a raw IMS multipleBatchCreations item into the shape
 * BatchSelectionModal's `mrps` prop expects, while preserving inventoryId.
 */
const normImsBatch = (b) => ({
  mrp: b?.mrp,
  sellingPrice: b?.sellingPrice,
  gtin: b?.gtin,
  batch: b?.batchId,
  batchNum: b?.batchId,
  batchNo: b?.batchId,
  offers: b?.offersList ? b : null,
  purchasePrice: b?.purchasePrice,
  isMultiple: b?.multipleBatches,
  availableUnits: b?.availableUnits,
  sellingUnit: b?.sellingUnit,
  expiryDate: b?.expiryDate,
  inventoryId: b?.inventoryId,
});

const MONTHS_BB = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const fmtBatchExpiry = (v) => {
  if (!v) return null;
  const d = new Date(isNaN(Number(v)) ? v : Number(v));
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS_BB[d.getMonth()]} ${d.getFullYear()}`;
};
const isBatchExpired = (v) => {
  if (!v) return false;
  const d = new Date(isNaN(Number(v)) ? v : Number(v));
  return !isNaN(d.getTime()) && d < new Date();
};

/** Placeholder SVG shown in item rows when there is no product image */
const ImagePlaceholder = ({ className }) => (
  <span className={className}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  </span>
);

const BundleBuilder = ({
  onCancel,
  onAddedToCart,
  locId,
  retailType,
  initialData,
  onBundleSaved,
  onBundleCreated,
}) => {
  const showSnackbar = useSnackbar();
  const { addItemWithCheck, addItemDirect } = useAddToCart();
  const {
    setCartItems,
    setCartTotalQty,
    setCgst,
    setSgst,
    setCess,
    setTotal,
    setSubTotal,
    setanimationBox,
  } = useContext(UserContext);
  const CartId = localStorage.getItem("CartId");
  const orgId = localStorage.getItem("orgId");

  /* ── state ── */
  const [bundleTitle, setBundleTitle] = useState(initialData?.title || buildDefaultBundleName());
  const [bundleTitleError, setBundleTitleError] = useState(false);
  const [bundleMrp, setBundleMrp] = useState(initialData?.mrp ?? "");
  const [bundleSp, setBundleSp] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchLoader, setSearchLoader] = useState(false);
  const [imsLoader, setImsLoader] = useState(false); // IMS inventory lookup in flight
  const [bundleItems, setBundleItems] = useState(initialData?.items || []);
  const [addingToCart, setAddingToCart] = useState(false);
  const [creatingBundle, setCreatingBundle] = useState(false);
  const [cartStep, setCartStep] = useState(null); // null | "creating" | "created" | "adding"
  const [createdBundle, setCreatedBundle] = useState(null); // success popup data
  const [popupAddingToCart, setPopupAddingToCart] = useState(false);
  const [bundleQty, setBundleQty] = useState(initialData?.qty ?? 1);

  /* ── batch picker state (multi-batch selection for non-weighing products) ── */
  const [batchPickerOpen, setBatchPickerOpen] = useState(false);
  const [batchPickerBatches, setBatchPickerBatches] = useState([]);
  const [pendingBatchProduct, setPendingBatchProduct] = useState(null);

  const searchInputRef = useRef(null);

  /* ── pre-fill when a saved bundle is loaded ── */
  useEffect(() => {
    if (initialData) {
      setBundleTitle(initialData?.title || "");
      setBundleItems(initialData?.items || []);
      setBundleMrp(initialData?.mrp ?? "");
      setBundleQty(initialData?.qty ?? 1);
      setBundleTitleError(false);
    }
  }, [initialData]);

  /* ── computed ── */
  const totalSaleValue = bundleItems?.reduce((s, i) => s + (parseFloat(i?.sp) || 0), 0);
  const totalGst = bundleItems?.reduce((s, i) => {
    const sp = parseFloat(i?.sp) || 0;
    const r = parseFloat(i?.gstPct) || 0;
    if (!r) return s;
    return s + sp - sp / (1 + r / 100);
  }, 0);
  const cgstAmount = totalGst / 2;
  const sgstAmount = totalGst / 2;

  /* ── product search ── */
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }
      setSearchLoader(true);
      FilterProduct({
        page: "1",
        pageSize: "20",
        query,
        storeLocations: [locId],
        storeIds: [orgId],
        flattenResponse: true,
        ...(retailType === "GROCERY" && { bundleIncluded: false }),
      })
        .then((res) => {
          setSearchLoader(false);
          setSearchResults(res?.data?.data?.data?.data || []);
          setShowResults(true);
        })
        .catch(() => setSearchLoader(false));
    }, 400),
    [locId, retailType]
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  /* ── weighing-scale modal (capture-only: sets the bundle item quantity) ── */
  // pendingWeighingProduct.current stores { product, imsBatch } so both
  // the product object and the resolved IMS batch flow into addProductToBundle.
  const pendingWeighingProduct = useRef(null);
  const { findWeighingItem, renderWeighingModal } = useWeighingItemModal({
    onQuantityConfirm: ({ quantity }) => {
      const pending = pendingWeighingProduct.current;
      pendingWeighingProduct.current = null;
      if (pending) addProductToBundle(pending?.product, quantity, pending?.imsBatch);
    },
  });

  /**
   * handleResultClick — called when user picks a product from search results.
   *
   * Flow for ALL products:
   *   1. Call IMS getInventoryData to get real batch list.
   *   2. Filter: keep batches where availableUnits > 0.
   *   3a. 0 available → "No inventory available" snackbar.
   *   3b. Weighing product (any batch count) → silently take the latest batch,
   *       then open the weighing modal for quantity capture.
   *   3c. 1 available non-weighing → auto-add silently.
   *   3d. 2+ available non-weighing → open BatchSelectionModal.
   */
  const handleResultClick = async (product) => {
    const gtin = product?.gtin || product?.variant?.barcodes?.[0] || "";
    const name = product?.variant?.name || product?.name || "Product";
    const isWeighing = isWeighingProduct(product);

    // Hide the dropdown immediately; show the IMS lookup spinner
    setShowResults(false);
    setImsLoader(true);

    try {
      const res = await getInventoryData(locId, gtin);
      const root = res?.data?.data?.data;
      const allBatches = root?.multipleBatchCreations || [];
      const available = allBatches?.filter((b) => (b?.availableUnits ?? 0) > 0);

      if (available?.length === 0) {
        showSnackbar(
          `No inventory available for "${name}". Select a product which has active inventory.`,
          "error"
        );
        return;
      }

      if (isWeighing) {
        // For weighing products: silently pick the latest batch regardless of batch count,
        // then open the weighing modal to capture quantity.
        const latestBatch = available[available?.length - 1];
        pendingWeighingProduct.current = { product, imsBatch: latestBatch };
        const unit = product?.variant?.weightUnit || product?.variant?.sellingUnit || "";
        if (findWeighingItem(gtin, name, "weighingScale", unit)) return;
        pendingWeighingProduct.current = null;
      } else if (available?.length === 1) {
        // Single batch — silent auto-add
        addProductToBundle(product, undefined, available[0]);
      } else {
        // Multiple batches — let user pick one
        setPendingBatchProduct(product);
        setBatchPickerBatches(available?.map(normImsBatch));
        setBatchPickerOpen(true);
      }
    } catch (_err) {
      showSnackbar(`Could not fetch inventory for "${name}". Please try again.`, "error");
    } finally {
      setImsLoader(false);
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  /**
   * addProductToBundle — adds a product (with its resolved IMS batch) to the bundle items list.
   *
   * @param {Object} product     — raw product from CMS search response
   * @param {number} [weighingQty] — captured weight/quantity (weighing products only)
   * @param {Object} imsBatch    — raw batch object from IMS multipleBatchCreations
   */
  const addProductToBundle = (product, weighingQty, imsBatch) => {
    const name = product?.variant?.name || product?.name || "Product";

    /* ── 1. Inventory availability check (from IMS batch) ── */
    if (!imsBatch?.inventoryId) {
      showSnackbar(`"${name}" has no inventory data and cannot be added to the bundle`, "error");
      return;
    }
    const availQty = parseFloat(imsBatch?.availableUnits ?? 0);
    if (availQty <= 0) {
      showSnackbar(`"${name}" is out of stock and cannot be added to the bundle`, "error");
      return;
    }

    /* ── 2. Fill from IMS batch (source of truth for pricing) ── */
    const uom = product?.variant?.weightUnit || product?.variant?.sellingUnit || "";
    const mrp = parseFloat(imsBatch?.mrp || 0);
    const baseSp = parseFloat(imsBatch?.sellingPrice || 0);
    const purchasePrice = parseFloat(imsBatch?.purchasePrice || 0);
    const gstPct = parseFloat(product?.variant?.gstPercentage || product?.gstPercentage || 0);
    const gtin = product?.gtin || product?.variant?.barcodes?.[0] || "";
    const image =
      (Array.isArray(product?.variant?.images)
        ? product?.variant?.images[0]
        : product?.variant?.images) || "";

    /* ── 3. Weighing scale flag ── */
    const isWeighingScale = isWeighingProduct(product);

    /* IMS batch identity */
    const imsInventoryId = imsBatch?.inventoryId;
    const batchNo = imsBatch?.batchNo || imsBatch?.batchNum || "";

    /* Weighing items arrive with the quantity captured in the weighing modal.
       Reject invalid quantities outright — never silently fall back to 1. */
    let qty = 1;
    if (weighingQty !== undefined) {
      const q = parseFloat(weighingQty);
      if (!Number.isFinite(q) || q <= 0) {
        showSnackbar(`Invalid quantity for "${name}" — item not added`, "error");
        return;
      }
      qty = q;
    }
    const finalSp = qty !== 1 ? Math.round(baseSp * qty * 100) / 100 : baseSp;

    setBundleItems((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        name,
        uom,
        mrp,
        baseSp,
        sp: finalSp,
        qty,
        gstPct,
        gtin,
        image,
        isWeighingScale,
        raw: product,
        imsInventoryId,
        batchNo,
        purchasePrice,
        availableUnits: imsBatch?.availableUnits ?? null,
        expiryDate: imsBatch?.expiryDate || null,
      },
    ]);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
    searchInputRef.current?.focus();
  };

  const removeItem = (id) => setBundleItems((prev) => prev?.filter((i) => i?.id !== id));
  const updateItemField = (id, field, value) =>
    setBundleItems((prev) => prev?.map((i) => (i?.id === id ? { ...i, [field]: value } : i)));

  /* qty change → recalculate sp from baseSp */
  const updateItemQty = (id, rawQty) =>
    setBundleItems((prev) =>
      prev?.map((i) => {
        if (i?.id !== id) return i;
        const q = parseFloat(rawQty) || 0;
        const sp = q > 0 ? Math.round(i.baseSp * q * 100) / 100 : "";
        return { ...i, qty: rawQty, sp };
      })
    );

  /* ── auto-populate bundle MRP from sum of item MRP × qty ── */
  useEffect(() => {
    if (bundleItems?.length === 0) return;
    const sum = bundleItems?.reduce(
      (s, i) => s + (parseFloat(i?.mrp) || 0) * (parseFloat(i?.qty) || 1),
      0
    );
    setBundleMrp(String(Math.round(sum * 100) / 100));
  }, [bundleItems]);

  /* ── auto-populate bundle selling price from sum of item selling prices ── */
  useEffect(() => {
    if (bundleItems?.length === 0) return;
    const sum = bundleItems?.reduce((s, i) => s + (parseFloat(i?.sp) || 0), 0);
    setBundleSp(String(Math.round(sum * 100) / 100));
  }, [bundleItems]);

  /* ── save bundle ── */
  const handleSaveBundle = () => {
    if (!bundleTitle.trim()) {
      setBundleTitleError(true);
      return;
    }
    if (bundleItems?.length === 0) {
      showSnackbar("Add at least one product before saving the bundle", "error");
      return;
    }
    const existing = loadSavedBundles();
    const bundle = {
      id: Date.now(),
      title: bundleTitle.trim(),
      mrp: bundleMrp,
      qty: bundleQty,
      items: bundleItems,
      savedAt: Date.now(),
    };
    const updated = [bundle, ...existing?.filter((b) => b?.title !== bundle?.title)].slice(0, 20);
    persistBundles(updated);
    showSnackbar(`Bundle "${bundle.title}" saved — reuse it from the search drawer`, "success");
    onBundleSaved?.();
  };

  /* ── shared bundle-creation logic (returns bundle info on success, throws on failure) ── */
  const performCreateBundle = async () => {
    const missingGtin = bundleItems?.filter((i) => !i.gtin);
    if (missingGtin?.length > 0) {
      throw new Error(
        `These products have no barcode and cannot be bundled: ${missingGtin
          ?.map((i) => `"${i?.name}"`)
          .join(", ")}`
      );
    }
    // Use imsInventoryId (from IMS API) instead of inventorySync.inventoryId
    const missingInventory = bundleItems?.filter((i) => !i?.imsInventoryId);
    if (missingInventory?.length > 0) {
      throw new Error(
        `These products have no inventory data: ${missingInventory
          ?.map((i) => `"${i?.name}"`)
          .join(", ")}. Re-add them from search.`
      );
    }

    let userDetails = {};
    try {
      userDetails = JSON.parse(localStorage.getItem("retailUserDetails")) || {};
    } catch {
      /* ignore */
    }
    const createdBy = userDetails?.uidx || "";
    const createdByName = [userDetails?.firstName, userDetails?.secondName]
      ?.filter(Boolean)
      .join(" ");

    const title = bundleTitle.trim();

    /* ── Get a unique barcode from the catalog service ── */
    let bundleBarcode;
    try {
      const bcRes = await GenerateBundleBarcode();
      bundleBarcode =
        bcRes?.data?.data?.barcode ||
        bcRes?.data?.data?.barcodeValue ||
        bcRes?.data?.data?.gtin ||
        (typeof bcRes?.data?.data === "string" ? bcRes.data.data : null) ||
        bcRes?.data?.barcode ||
        null;
      if (!bundleBarcode) throw new Error("empty response");
    } catch (_err) {
      /* Fall back to timestamp barcode if the API is unavailable */
      bundleBarcode = generateBundleBarcodeFallback();
    }

    const qty = parseInt(bundleQty, 10) > 0 ? parseInt(bundleQty, 10) : 1;

    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30);

    const catSource =
      bundleItems?.map((i) => i.raw)?.find((p) => p?.appCategories?.categoryLevel1?.length) || {};
    const app = catSource?.appCategories || {};
    const lvl = (names, ids) => ({
      names: names?.length ? [names[0]] : [],
      ids: ids?.length ? [ids[0]] : [],
    });
    const l1 = lvl(app?.categoryLevel1, app?.categoryLevel1Id);
    const l2 = lvl(app?.categoryLevel2, app?.categoryLevel2Id);
    const l3 = lvl(app?.categoryLevel3, app?.categoryLevel3Id);

    const bundlePrice = parseFloat(bundleSp) || Math.round(totalSaleValue * 100) / 100;
    const mrpValue = parseFloat(bundleSp) || bundlePrice;

    // Purchase total from IMS batch purchase prices (not inventorySync)
    const purchaseTotal = bundleItems?.reduce((s, i) => s + (parseFloat(i?.purchasePrice) || 0), 0);
    const profit = Math.round((bundlePrice - purchaseTotal) * 100) / 100;
    const marginPct = purchaseTotal > 0 ? Math.round((profit / purchaseTotal) * 10000) / 100 : 0;

    // CMS payload — unchanged structure; uses inventorySync from raw product for catalog metadata
    const cmsPayload = {
      productName: title,
      action: "CREATE_BUNDLE",
      logType: "ProductLog",
      product: {
        name: title,
        title,
        shortDescription: "",
        description: "",
        bundlePrice: parseFloat(bundleMrp) || bundlePrice,
        appCategories: {
          categoryLevel1: l1?.names,
          categoryLevel1Id: l1?.ids,
          categoryLevel2: l2?.names,
          categoryLevel2Id: l2?.ids,
          categoryLevel3: l3?.names,
          categoryLevel3Id: l3?.ids,
        },
        posCategories: {
          categoryLevel1: l1?.names,
          categoryLevel1Id: l1?.ids,
          categoryLevel2: l2?.names,
          categoryLevel2Id: l2?.ids,
          categoryLevel3: l2?.names?.length ? l2?.names : l3?.names,
          categoryLevel3Id: l2?.ids?.length ? l2?.ids : l3?.ids,
        },
        returnable: false,
        isBundle: true,
        bundleBarcode,
        bundleValidity: "",
        bundleQuantity: qty,
        bundleStartDate: startDate.getTime(),
        bundleEndDate: endDate.getTime(),
        seoTags: [],
        tags: [],
        dimensions: { measurementUnit: "" },
        taxReference: {
          taxName: "GST & CESS",
          taxType: "GST",
          taxCategory: "GST",
          taxRate: 0,
          isDefault: true,
          metadata: { hsnCode: "", cess: "" },
        },
        attributes: {},
        storeSpecificData: {
          storeId: orgId,
          storeLocationId: locId,
          storeName: orgId,
          storeLocationName: locId,
        },
        minutesToReturnBack: "",
        bundleSalesChannels: ["IN_STORE"],
        externalSalesChannels: [
          {
            salesChannelName: "IN_STORE",
            salePrice: String(bundlePrice),
            marginPercentage: marginPct,
            profit,
          },
        ],
        variants: bundleItems?.map((item) => {
          const v = item?.raw?.variant || {};
          const inv = v?.inventorySync || {};
          return {
            variantId: v?.variantId || "",
            name: v?.name || item?.name,
            barcodes: v?.barcodes?.length ? v?.barcodes : [item?.gtin],
            sku: bundleBarcode,
            weight: v?.weight || "",
            weightUnit: v?.weightUnit || "",
            packType: v?.packType || "",
            images: {},
            mrpData: v?.mrpData?.length
              ? v.mrpData
              : [{ currencyCode: "INR", currencySymbol: "₹", mrp: item?.mrp, country: "India" }],
            inventorySync: { ...inv, bundleQuantity: String(parseFloat(item.qty) || 1) },
            isActive: true,
            salesChannels: ["IN_STORE"],
            externalSalesChannels: [
              {
                id: "IN_STORE",
                consumable: [],
                salePrice: String(parseFloat(item?.sp) || 0),
                margin: marginPct,
                profit,
              },
            ],
            listedOn: [],
            tags: [],
            createdBy,
            createdByName,
          };
        }),
        nativeLanguages: [{ language: "", name: "" }],
        imageUrls: {},
        createdBy,
        updatedBy: createdBy,
        createdByName,
      },
      sourceId: orgId,
      sourceLocationId: locId,
    };

    // IMS payload — uses data sourced entirely from IMS API (imsInventoryId, mrp, purchasePrice from batch)
    const imsPayload = {
      bundleGtin: bundleBarcode,
      title,
      categoryLevel1: l1?.names[0] || "",
      categoryLevel2: l2?.names[0] || "",
      categoryLevel3: l3?.names[0] || "",
      description: "",
      organizationId: orgId,
      batchSelection: true,
      returnable: false,
      quantity: qty,
      createdBy,
      createdByName,
      bundleRequests: {
        [locId]: [
          {
            locationId: locId,
            mrp: parseFloat(bundleMrp) || mrpValue,
            purchasePrice: purchaseTotal,
            sellingPrice: bundlePrice,
            totalQuantity: parseFloat(qty) || 1,
            startDate: toYMD(startDate),
            endDate: toYMD(endDate),
            bundleProducts: bundleItems?.map((item) => {
              const itemQty = parseFloat(item?.qty) || 1;
              return {
                gtin: item?.gtin,
                inventoryIds: item?.imsInventoryId ? [item?.imsInventoryId] : [],
                quantity: itemQty,
                mrp: parseFloat(item?.mrp) || 0,
                sellingPrice: parseFloat(item?.sp) || 0,
                purchasePrice: parseFloat(item?.purchasePrice) || 0,
                uom: item?.uom || item?.raw?.variant?.weightUnit || "",
                sellingUnit:
                  item?.uom ||
                  item?.raw?.variant?.sellingUnit ||
                  item?.raw?.variant?.weightUnit ||
                  "",
              };
            }),
          },
        ],
      },
    };

    const cmsRes = await CreateBundleCMS(cmsPayload);
    if (cmsRes?.data?.data?.es !== 0) {
      throw new Error(cmsRes?.data?.data?.message || "catalog error");
    }

    const imsRes = await CreateBundleIMS(imsPayload);
    if (imsRes?.data?.data?.es !== 0) {
      throw new Error(
        `Catalog saved, but inventory failed: ${imsRes?.data?.data?.message || "inventory error"}`
      );
    }

    return {
      title,
      bundleBarcode,
      bundlePrice,
      mrpValue,
      startDate,
      endDate,
      itemCount: bundleItems?.length,
    };
  };

  /* ── create bundle (green button) → show success popup ── */
  const handleCreateBundle = async () => {
    if (!bundleTitle.trim()) {
      setBundleTitleError(true);
      return;
    }
    if (bundleItems?.length === 0) {
      showSnackbar("Add at least one product before creating the bundle", "error");
      return;
    }
    setCreatingBundle(true);
    try {
      const info = await performCreateBundle();
      setCreatedBundle(info);
    } catch (err) {
      showSnackbar(
        `Bundle creation failed: ${
          err?.response?.data?.message || err?.message || "unknown error"
        }`,
        "error"
      );
    } finally {
      setCreatingBundle(false);
    }
  };

  /* ── "Add to cart" button → Step 1: create bundle, Step 2: add to cart ── */
  const handleAddToCartWithCreate = async () => {
    if (!bundleTitle.trim()) {
      setBundleTitleError(true);
      return;
    }
    if (bundleItems?.length === 0) {
      showSnackbar("Add at least one product to the bundle", "error");
      return;
    }

    setCartStep("creating");
    let info;
    try {
      info = await performCreateBundle();
    } catch (err) {
      showSnackbar(
        `Bundle creation failed: ${
          err?.response?.data?.message || err?.message || "unknown error"
        }`,
        "error"
      );
      setCartStep(null);
      return;
    }

    setCartStep("created");
    await new Promise((r) => setTimeout(r, 750));
    setCartStep("adding");

    await addItemWithCheck(
      { cartId: CartId, gtin: info?.bundleBarcode, locationId: locId, orgId },
      {
        onBeforeError: () => setCartStep(null),
        onError: (errMsg) => {
          showSnackbar(`Bundle created but could not be added to cart: ${errMsg}`, "error");
          setCreatedBundle(info);
        },
        onSuccess: () => {
          showSnackbar(`Bundle "${info?.title}" created and added to cart`, "success");
          onAddedToCart();
        },
        /* If /add/pos returns batches, auto-pick the first one */
        onBatchSelect: async (dataArr) => {
          const batch = dataArr[0];
          if (!batch) {
            setCreatedBundle(info);
            return;
          }
          await addItemDirect(
            {
              cartId: CartId,
              gtin: batch?.gtin || info?.bundleBarcode,
              sellingPrice: batch?.sellingPrice ?? info?.bundlePrice,
              locationId: locId,
              mrp: batch?.mrp ?? info?.mrpValue,
              batchNo: batch?.batchNo || batch?.batch || "",
              inventoryChecks: "NO",
              isOfferAvailable: false,
              variantName: info?.title,
              orgId,
            },
            [],
            {
              onError: (errMsg) => {
                showSnackbar(`Bundle created but could not be added to cart: ${errMsg}`, "error");
                setCreatedBundle(info);
              },
              onSuccess: () => {
                showSnackbar(`Bundle "${info?.title}" created and added to cart`, "success");
                onAddedToCart();
              },
            }
          );
        },
      }
    );
    setCartStep(null);
  };

  /* ── success popup: add bundle to cart ── */
  const handlePopupAddToCart = async () => {
    if (!createdBundle) return;
    setPopupAddingToCart(true);
    await addItemWithCheck(
      { cartId: CartId, gtin: createdBundle?.bundleBarcode, locationId: locId, orgId },
      {
        onBeforeError: () => setPopupAddingToCart(false),
        onError: (errMsg) => {
          showSnackbar(`Could not add to cart: ${errMsg}`, "error");
        },
        onSuccess: () => {
          showSnackbar(`Bundle "${createdBundle?.title}" added to cart`, "success");
          setCreatedBundle(null);
          onBundleCreated?.();
          setPopupAddingToCart(false);
        },
        /* If /add/pos returns batches, auto-pick the first one */
        onBatchSelect: async (dataArr) => {
          const batch = dataArr[0];
          if (!batch) {
            setPopupAddingToCart(false);
            return;
          }
          await addItemDirect(
            {
              cartId: CartId,
              gtin: batch?.gtin || createdBundle?.bundleBarcode,
              sellingPrice: batch?.sellingPrice ?? createdBundle?.bundlePrice,
              locationId: locId,
              mrp: batch?.mrp ?? createdBundle?.mrpValue,
              batchNo: batch?.batchNo || batch?.batch || "",
              inventoryChecks: "NO",
              isOfferAvailable: false,
              variantName: createdBundle?.title,
              orgId,
            },
            [],
            {
              onBeforeError: () => setPopupAddingToCart(false),
              onError: (errMsg) => {
                showSnackbar(`Could not add to cart: ${errMsg}`, "error");
              },
              onSuccess: () => {
                showSnackbar(`Bundle "${createdBundle?.title}" added to cart`, "success");
                setCreatedBundle(null);
                onBundleCreated?.();
                setPopupAddingToCart(false);
              },
            }
          );
        },
      }
    );
    setPopupAddingToCart(false);
  };

  /* ── cart state updater ── */
  const applyCartResponse = (res) => {
    const cartData = res?.data?.data?.data || res?.data?.data;
    const billing = cartData?.billing;
    const cartProducts = cartData?.cartProducts;
    if (cartProducts) {
      setCartItems(cartProducts?.sort((a, b) => b?.modifiedDate.localeCompare(a?.modifiedDate)));
      setCartTotalQty(billing?.quantity);
      setCgst(billing?.cgst);
      setSgst(billing?.sgst);
      setCess(billing?.totalCessAmount);
      setTotal(billing?.totalCartValue);
      setSubTotal(billing?.subtotal);
      setanimationBox?.(true);
    }
  };

  /* ── add to cart ── */
  const handleAddToCart = async () => {
    if (!bundleTitle.trim()) {
      setBundleTitleError(true);
      return;
    }
    if (bundleItems?.length === 0) {
      showSnackbar("Add at least one product to the bundle", "error");
      return;
    }

    const missingGtin = bundleItems?.filter((i) => !i?.gtin);
    if (missingGtin?.length > 0) {
      const names = missingGtin?.map((i) => `"${i.name}"`).join(", ");
      showSnackbar(
        `${
          missingGtin?.length === 1 ? "This product has" : "These products have"
        } no barcode and cannot be added to the cart: ${names}. Remove ${
          missingGtin?.length === 1 ? "it" : "them"
        } from the bundle to continue.`,
        "error"
      );
      return;
    }

    setAddingToCart(true);
    let addedCount = 0;

    try {
      for (const item of bundleItems) {
        const specSuffix = [item?.spec, item?.itemUom]?.filter(Boolean).join(" ");
        const variantName = `[${bundleTitle}] ${item?.name}${specSuffix ? ` — ${specSuffix}` : ""}`;

        let itemErrorOccurred = false;
        await addItemWithCheck(
          { cartId: CartId, gtin: item?.gtin, locationId: locId, orgId },
          {
            onBeforeError: () => {
              itemErrorOccurred = true;
            },
            onError: (errMsg) => {
              const partialMsg =
                addedCount > 0
                  ? ` — ${addedCount} of ${bundleItems?.length} items were already added to the cart.`
                  : "";
              showSnackbar(
                `Failed to add "${item?.name}" to cart${partialMsg} ${errMsg}`.trim(),
                "error"
              );
            },
            onSuccess: () => {
              addedCount++;
            },
            /* If /add/pos returns batches, auto-pick the first one */
            onBatchSelect: async (dataArr) => {
              const batch = dataArr[0];
              if (!batch) {
                itemErrorOccurred = true;
                return;
              }
              await addItemDirect(
                {
                  cartId: CartId,
                  gtin: batch?.gtin || item?.gtin,
                  sellingPrice: batch?.sellingPrice ?? (parseFloat(item?.sp) || item.mrp),
                  locationId: locId,
                  mrp: batch.mrp ?? item?.mrp,
                  batchNo: batch.batchNo || batch?.batch || "",
                  inventoryChecks: "NO",
                  isOfferAvailable: false,
                  variantName,
                  orgId,
                },
                [],
                {
                  onBeforeError: () => {
                    itemErrorOccurred = true;
                  },
                  onError: (errMsg) => {
                    const partialMsg =
                      addedCount > 0
                        ? ` — ${addedCount} of ${bundleItems?.length} items were already added to the cart.`
                        : "";
                    showSnackbar(
                      `Failed to add "${item?.name}" to cart${partialMsg} ${errMsg}`.trim(),
                      "error"
                    );
                  },
                  onSuccess: () => {
                    addedCount++;
                  },
                }
              );
            },
          }
        );

        if (itemErrorOccurred) return;
      }

      showSnackbar(
        `Bundle "${bundleTitle}" added to cart (${addedCount} item${addedCount !== 1 ? "s" : ""})`,
        "success"
      );
      onAddedToCart();
    } finally {
      setAddingToCart(false);
    }
  };

  /* ── render ── */
  return (
    <div className="bb-root">
      {renderWeighingModal()}

      {/* ── Batch selection modal (multi-batch non-weighing products) ── */}
      <BatchSelectionModal
        open={batchPickerOpen}
        onClose={() => {
          setBatchPickerOpen(false);
          setPendingBatchProduct(null);
          setBatchPickerBatches([]);
        }}
        mrps={batchPickerBatches}
        selectedProduct={pendingBatchProduct?.variant?.name || pendingBatchProduct?.name || ""}
        selectedGtin={
          pendingBatchProduct?.gtin || pendingBatchProduct?.variant?.barcodes?.[0] || ""
        }
        handlePrice={() => {}} // no-op — bundle mode doesn't use the price-bubble pattern
        onBatchConfirm={(batch) => {
          if (pendingBatchProduct) addProductToBundle(pendingBatchProduct, undefined, batch);
          setBatchPickerOpen(false);
          setPendingBatchProduct(null);
          setBatchPickerBatches([]);
        }}
        addItemWithScanner={() => {}} // never called when onBatchConfirm is provided
        disabled={false}
        selectedMrp={0}
        SelectedSellingPrice={0}
        batchNo=""
        purchasePrice={0}
        sellingUnit=""
        offerData={null}
        availableUnits={0}
        expiryDate={null}
        brandName=""
        packagingType=""
        variantName=""
        vendorId=""
      />

      <div className="bb-body">
        {/* Bundle name */}
        <div className="bb-section">
          <label className="bb-label" htmlFor="bundle-title">
            Bundle name <span className="bb-required">*</span>
          </label>
          <div className="bb-title-input-wrap">
            <input
              id="bundle-title"
              className={`bb-input${bundleTitleError ? " bb-input--error" : ""}`}
              placeholder="e.g. Breakfast Pack, Party Bundle…"
              value={bundleTitle}
              onChange={(e) => {
                setBundleTitle(e.target.value);
                if (e.target.value) setBundleTitleError(false);
              }}
              autoFocus
            />
            {bundleTitle && (
              <button
                type="button"
                className="bb-title-clear"
                onClick={() => {
                  setBundleTitle("");
                  setBundleTitleError(false);
                }}
                aria-label="Clear bundle name"
              >
                ×
              </button>
            )}
          </div>
          {bundleTitleError && <p className="bb-error-msg">Bundle name is required</p>}
        </div>

        {/* Product search */}
        <div className="bb-section">
          <label className="bb-label">Add products</label>
          <div className="bb-search-wrap">
            <div className="bb-search-input-row">
              {imsLoader ? (
                <CircularProgress size={14} sx={{ color: "#2a71ff", flexShrink: 0 }} />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="bb-search-icon"
                  aria-hidden="true"
                >
                  <path d="m21 21-4.34-4.34" />
                  <circle cx="11" cy="11" r="8" />
                </svg>
              )}
              <input
                ref={searchInputRef}
                className="bb-search-input"
                placeholder={imsLoader ? "Checking inventory…" : "Search for a product to add…"}
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={() => setTimeout(() => setShowResults(false), 180)}
                onFocus={() => searchResults?.length > 0 && setShowResults(true)}
                disabled={imsLoader}
              />
              {searchLoader && !imsLoader && (
                <CircularProgress size={15} sx={{ color: "#64748b", flexShrink: 0, ml: "4px" }} />
              )}
              {searchQuery && !searchLoader && !imsLoader && (
                <button
                  type="button"
                  className="bb-search-clear"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSearchQuery("");
                    setSearchResults([]);
                    setShowResults(false);
                    searchInputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            {showResults && searchResults?.length > 0 && (
              <div className="bb-search-results">
                {searchResults.slice(0, 8)?.map((product, i) => {
                  const name = product?.variant?.name || product?.name || "";
                  const image =
                    (Array.isArray(product?.variant?.images)
                      ? product?.variant.images[0]
                      : product?.variant?.images) || "";
                  const sp = product?.variant?.inventorySync?.sellingPrice;
                  const mrp = product?.variant?.inventorySync?.mrp;
                  const uom = product?.variant?.weightUnit || "";
                  const isWeighing = isWeighingProduct(product);
                  return (
                    <button
                      key={i}
                      type="button"
                      className="bb-result-item"
                      onMouseDown={() => handleResultClick(product)}
                    >
                      <div className="bb-result-img">
                        {image ? (
                          <img src={image} alt={name} className="bb-result-img-el" />
                        ) : (
                          <ImagePlaceholder className="bb-result-placeholder" />
                        )}
                      </div>
                      <div className="bb-result-info">
                        <div className="bb-result-title-row">
                          <p className="bb-result-name">{name}</p>
                          {isWeighing && (
                            <span
                              className="product-search-badge-scale"
                              data-testid={`status-weighing-scale-bb-${i}`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="product-search-scale-icon"
                                aria-hidden="true"
                              >
                                <circle cx="12" cy="5" r="3"></circle>
                                <path d="M6.5 8a2 2 0 0 0-1.905 1.46L2.1 18.5A2 2 0 0 0 4 21h16a2 2 0 0 0 1.925-2.54L19.4 9.5A2 2 0 0 0 17.48 8Z"></path>
                              </svg>
                              Scale
                            </span>
                          )}
                          {isWeighing && uom && (
                            <span
                              className="product-search-badge-weight-unit"
                              data-testid={`status-weight-unit-bb-${i}`}
                            >
                              {formatWeightUnit(uom)}
                            </span>
                          )}
                        </div>
                        {uom && !isWeighing && <span className="bb-result-uom">{uom}</span>}
                      </div>
                      <div className="bb-result-prices">
                        {mrp && <span className="bb-result-mrp">₹{mrp}</span>}
                        {sp && <span className="bb-result-sp">₹{sp}</span>}
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#2a71ff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            )}
            {showResults && searchResults?.length === 0 && !searchLoader && searchQuery && (
              <div className="bb-no-results">No products found for "{searchQuery}"</div>
            )}
          </div>
        </div>

        {/* Line items */}
        <div className="bb-items-section">
          <div className="bb-items-header">
            <span className="bb-label">Bundle items</span>
            {bundleItems?.length > 0 && (
              <span className="bb-items-count">
                {bundleItems?.length} item{bundleItems?.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {bundleItems?.length === 0 ? (
            <div className="bb-empty-items">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#cbd5e1"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.29 7 12 12 20.71 7" />
                <line x1="12" y1="22" x2="12" y2="12" />
              </svg>
              <p>Search and add products above to build your bundle</p>
            </div>
          ) : (
            <div className="bb-items-list">
              {bundleItems?.map((item, idx) => (
                <div key={item?.id} className="bb-item-row">
                  {/* serial number */}
                  <div className="bb-item-serial">{idx + 1}</div>

                  {/* name + meta */}
                  <div className="bb-item-info">
                    <p className="bb-item-name">{item?.name}</p>
                    <div className="bb-item-meta">
                      <span className="bb-item-mrp">
                        MRP ₹{parseFloat(item?.mrp || 0).toFixed(2)}
                      </span>
                      <span className="bb-item-pp">
                        PP ₹
                        {parseFloat(
                          item?.purchasePrice ||
                            item?.raw?.variant?.inventorySync?.purchasePrice ||
                            0
                        ).toFixed(2)}
                      </span>
                      <span
                        className="bb-item-batch"
                        title={item?.batchNo ? `Batch: ${item?.batchNo}` : "No batch ID"}
                      >
                        #{item?.batchNo || "—"}
                      </span>
                      {item?.availableUnits != null && (
                        <span className="bb-item-avail">Avl: {item?.availableUnits}</span>
                      )}
                      {item?.expiryDate && (
                        <span
                          className={`bb-item-expiry${
                            isBatchExpired(item?.expiryDate) ? " bb-item-expiry--expired" : ""
                          }`}
                        >
                          Exp: {fmtBatchExpiry(item?.expiryDate)}
                        </span>
                      )}
                      {item?.uom && <span className="bb-item-uom-pill">{item?.uom}</span>}
                      {item.isWeighingScale && (
                        <span
                          className="bb-item-scale-pill"
                          title="Weighing scale product — decimal qty allowed"
                        >
                          ⚖ Scale
                        </span>
                      )}
                      {!item?.gtin && (
                        <span
                          className="bb-item-no-barcode"
                          title="No barcode — cannot be added to cart"
                        >
                          ⚠ No barcode
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Qty */}
                  <div className="bb-item-qty-wrap">
                    <label className="bb-item-qty-label">Qty</label>
                    <div className="bb-item-qty-row">
                      <input
                        className="bb-item-qty-input"
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateItemQty(item.id, e.target.value)}
                        min="0.001"
                        step="any"
                      />
                      {item.uom && <span className="bb-item-qty-uom">{item.uom}</span>}
                    </div>
                  </div>

                  {/* Sale price */}
                  <div className="bb-item-sp-wrap">
                    <label className="bb-item-sp-label">Sale ₹</label>
                    <input
                      className="bb-item-sp-input"
                      type="number"
                      value={item.sp}
                      onChange={(e) => updateItemField(item.id, "sp", e.target.value)}
                      min="0"
                    />
                  </div>

                  {/* remove */}
                  <button
                    type="button"
                    className="bb-item-remove"
                    onClick={() => removeItem(item.id)}
                    title="Remove"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
              ))}

              <p className="bb-add-more-hint">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
                Search above to add more products
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Summary footer ── */}
      {bundleItems.length > 0 && (
        <div className="bb-summary">
          {/* header row */}
          <div className="bb-summary-header">
            <span className="bb-summary-title">Bundle summary</span>
            <span className="bb-summary-items-badge">
              {bundleItems.length} item{bundleItems.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* receipt-style breakdown */}
          <div className="bb-receipt">
            {/* Bundle MRP — read-only */}
            <div className="bb-receipt-row">
              <span className="bb-receipt-label">Bundle MRP</span>
              <div className="bb-receipt-input-wrap">
                <span className="bb-receipt-currency">₹</span>
                <input
                  className="bb-receipt-input"
                  type="number"
                  min="0"
                  placeholder="0.00"
                  value={bundleMrp}
                  readOnly
                />
              </div>
            </div>

            {/* Bundle Selling Price — editable */}
            <div className="bb-receipt-row">
              <span className="bb-receipt-label">Bundle Selling Price</span>
              <div className="bb-receipt-input-wrap">
                <span className="bb-receipt-currency">₹</span>
                <input
                  className="bb-receipt-input"
                  type="number"
                  min="0"
                  placeholder="0.00"
                  value={bundleSp}
                  onChange={(e) => setBundleSp(e.target.value)}
                />
              </div>
            </div>

            {/* Bundle quantity — how many bundles can be sold */}
            <div className="bb-receipt-row">
              <span className="bb-receipt-label">Bundle quantity</span>
              <div className="bb-receipt-input-wrap">
                <input
                  className="bb-receipt-input"
                  type="number"
                  min="1"
                  placeholder="1"
                  value={bundleQty}
                  onChange={(e) => setBundleQty(e.target.value)}
                />
              </div>
            </div>

            <div className="bb-receipt-sep" />

            {/* Sale price (sum of line items) */}
            <div className="bb-receipt-row">
              <span className="bb-receipt-label">Sale price</span>
              <span className="bb-receipt-value">₹{totalSaleValue.toFixed(2)}</span>
            </div>

            {/* GST breakup */}
            <div className="bb-receipt-row bb-receipt-row--sub">
              <span className="bb-receipt-label">CGST</span>
              <span className="bb-receipt-value bb-receipt-value--muted">
                ₹{cgstAmount.toFixed(2)}
              </span>
            </div>
            <div className="bb-receipt-row bb-receipt-row--sub">
              <span className="bb-receipt-label">SGST</span>
              <span className="bb-receipt-value bb-receipt-value--muted">
                ₹{sgstAmount.toFixed(2)}
              </span>
            </div>

            <div className="bb-receipt-sep" />

            {/* Total sale value — reflects bundle SP override if entered */}
            <div className="bb-receipt-row bb-receipt-row--total">
              <span className="bb-receipt-label bb-receipt-label--bold">Total sale value</span>
              <span className="bb-receipt-value bb-receipt-value--highlight">
                ₹{(parseFloat(bundleSp) || totalSaleValue).toFixed(2)}
              </span>
            </div>
          </div>

          {/* action buttons */}
          <div className="bb-footer-btns">
            <button
              type="button"
              className="bb-cancel-btn"
              onClick={onCancel}
              disabled={addingToCart || creatingBundle || !!cartStep}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bb-create-btn"
              onClick={handleCreateBundle}
              disabled={addingToCart || creatingBundle || !!cartStep}
              title="Create bundle in catalog & inventory"
            >
              {creatingBundle ? (
                <>
                  <CircularProgress size={14} sx={{ color: "#fff" }} /> Creating…
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                  Create bundle
                </>
              )}
            </button>
            <button
              type="button"
              className="bb-add-cart-btn"
              onClick={handleAddToCartWithCreate}
              disabled={addingToCart || creatingBundle || !!cartStep}
            >
              {cartStep === "creating" && (
                <>
                  <CircularProgress size={14} sx={{ color: "#fff" }} /> Creating bundle…
                </>
              )}
              {cartStep === "created" && (
                <span className="bb-step-done">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  Bundle created!
                </span>
              )}
              {cartStep === "adding" && (
                <>
                  <CircularProgress size={14} sx={{ color: "#fff" }} /> Adding to cart…
                </>
              )}
              {!cartStep && (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                  </svg>
                  Add to cart
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Bundle created success popup ── */}
      {createdBundle && (
        <div
          className="bb-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Bundle created"
        >
          <div className="bb-popup">
            {/* check icon */}
            <div className="bb-popup-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>

            <h3 className="bb-popup-title">Bundle Created!</h3>
            <p className="bb-popup-subtitle">Your bundle is live and ready to sell.</p>

            {/* details card */}
            <div className="bb-popup-details">
              <p className="bb-popup-name">{createdBundle.title}</p>
              <div className="bb-popup-meta">
                <span className="bb-popup-chip">#{createdBundle.bundleBarcode}</span>
                <span className="bb-popup-chip">
                  {createdBundle.itemCount} item{createdBundle.itemCount !== 1 ? "s" : ""}
                </span>
                <span className="bb-popup-chip bb-popup-chip--price">
                  ₹{createdBundle.bundlePrice.toFixed(2)}
                </span>
              </div>
              <p className="bb-popup-validity">
                Valid:{" "}
                {createdBundle.startDate.toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
                {" → "}
                {createdBundle.endDate.toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>

            {/* action buttons */}
            <div className="bb-popup-btns">
              <button
                type="button"
                className="bb-popup-cart-btn"
                disabled={popupAddingToCart}
                onClick={handlePopupAddToCart}
              >
                {popupAddingToCart ? (
                  <>
                    <CircularProgress size={14} sx={{ color: "#fff" }} /> Adding…
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    Add to cart
                  </>
                )}
              </button>
              <button
                type="button"
                className="bb-popup-done-btn"
                disabled={popupAddingToCart}
                onClick={() => {
                  setCreatedBundle(null);
                  onBundleCreated?.();
                }}
              >
                View bundles
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BundleBuilder;
